import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, Layers, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  calibrated: "bg-emerald-600 text-white",
  calibrated_draft: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  partial: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  developing: "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200",
};

const StatCard = ({ label, value, testid }) => (
  <div className="ek-surface-2 border ek-line rounded-lg p-3" data-testid={testid}>
    <div className="text-[22px] font-bold ek-text leading-none tabular-nums">{value}</div>
    <div className="text-[11px] ek-text-muted mt-1 leading-tight">{label}</div>
  </div>
);

export default function CalibrationMapPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enterprise360/calibration-map");
      setData(data);
    } catch {
      toast.error("Could not load the calibration map");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  return (
    <div className="space-y-5" data-testid="calibration-map-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold ek-text flex items-center gap-2">
            <ShieldCheck size={18} className="text-[var(--e360-primary,#7737F6)]" /> Sector &amp; archetype calibration map
          </h2>
          <p className="text-[13px] ek-text-soft mt-1 max-w-3xl">
            An honest, self-derived statement of how deeply the engine is calibrated for each business
            archetype — authored value-stream breadth plus the live-engagement evidence base. This is the
            benchmark we publish rather than over-claim: calibration strengthens as engagements accrue.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="calibration-refresh"
          className="inline-flex items-center gap-1 text-[12px] ek-text-muted hover:opacity-70 transition-opacity">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Archetypes calibrated" value={s.total_archetypes} testid="calib-total" />
          <StatCard label="Calibrated v1" value={s.by_status.calibrated} testid="calib-v1" />
          <StatCard label="Calibrated (draft)" value={s.by_status.calibrated_draft} testid="calib-draft" />
          <StatCard label="Field-validated" value={s.with_engagements} testid="calib-field" />
          <StatCard label="Live engagements" value={s.total_engagements} testid="calib-engagements" />
        </div>
      )}

      {loading && !data ? (
        <div className="text-[13px] ek-text-muted py-6">Loading…</div>
      ) : data ? (
        <div className="ek-surface border ek-line rounded-lg overflow-hidden">
          <table className="w-full text-[13px]" data-testid="calibration-table">
            <thead>
              <tr className="ek-surface-2 text-left ek-text-soft">
                <th className="p-3 font-medium">Archetype</th>
                <th className="p-3 font-medium">Sector(s)</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Confidence</th>
                <th className="p-3 font-medium">Value streams</th>
                <th className="p-3 font-medium">Overlays</th>
                <th className="p-3 font-medium">Evidence base</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t ek-line align-top" data-testid={`calib-row-${r.id}`}>
                  <td className="p-3 ek-text font-medium">{r.name}</td>
                  <td className="p-3 ek-text-muted">{(r.sectors || []).join(", ").replace(/_/g, " ") || "sector-agnostic"}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>
                      {r.status_label}
                    </span>
                  </td>
                  <td className="p-3 ek-text-soft">{r.confidence}</td>
                  <td className="p-3 ek-text-soft">
                    <span className="inline-flex items-center gap-1"><Layers size={12} /> {r.authored_variants}/{r.active_streams} authored</span>
                  </td>
                  <td className="p-3 ek-text-soft tabular-nums">{r.overlay_count}</td>
                  <td className="p-3 ek-text-soft">
                    {r.engagements > 0 && <CheckCircle2 size={12} className="inline text-emerald-600 mr-1" />}
                    {r.evidence_base}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
